import type { Address } from '~~/types/commercetools';

import { CheckoutClientType } from '~~/types/checkout/formData/checkoutClientType';

import { getVatNumberFromCustomerAddress } from '~~/helpers/getVatNumberFromCustomerAddress';
import registerAddressErrors from '~~/helpers/Checkout/registerAddressErrors/registerAddressErrors';
import type { ValidationProvider } from '~~/types/vee-validate';
import { useBilling, useUser } from '~/utils/ct';
import type { VatCompanyAddress } from '~~/types/vatValidation';
import { asyncDebounce } from '~~/helpers/lodash/asyncDebounce';
import {
  hasRegistryAddressToValidate,
  VAT_AUTOFILL_ADDRESS_FIELDS,
  isFieldUnanswered,
} from '~~/helpers/vatValidation/getAddressMismatch';
import { ShippingAddressType } from '~~/types/checkout/ShippingAddressType';
import { ADDRESS_PERSISTENCE_DEBOUNCE_MS } from '~~/constants/checkout';
import { truncateAddressFields } from '~~/helpers/address/truncateAddressFields';
import { ADDRESS_TYPE } from '~~/types/AddressType';

const useCheckoutBilling = () => {

  const {
    billing: address,
    load: loadBilling,
  } = useBilling();
  const { load: loadUser } = useUser();
  const { clientType, setClientTypeFromAddress } = useCheckoutClientType('Billing');
  const { defaultAddress } = useCheckout();
  const {
    cart,
    setBillingDetails: setCartBillingDetails,
  } = useCartExtended();
  const { countryCode: country } = useLocalization();
  const {
    validateVatNumber,
    validateAddress,
    markAddressAsValidated,
    vatResult,
    companyAddress,
    addressMismatches,
    validatingAddressFields,
    shouldClearRegistryAddress,
  } = useVatValidation();
  const { isBillingAddressValidationEnabled } = useStoreVsfExtended();

  const {
    isAddressAutofilled,
    hasAddressBeenAutofilled,
    resetAddressAutofill,
    setAddressAutofilled,
    dismissAutofillNotice,
  } = useVatAutofillAddress();

  const { billingDetails, setBillingDetailsMutation } = useBillingDetailsStore();
  const persistedBillingDetails = useState<Partial<Address>>('persistedBillingDetails', () => ({}));
  const { shippingAddressType } = useShippingAddressTypeStore();
  const { vatNumber, setVatNumber: setAddressVatNumber } = useCheckoutAddressVat('Billing');
  const useCheckoutAddressInstance = useCheckoutAddress({
    addressType: 'Billing',
    scrollToTopOfForm: '#BillingForm',
    addressDetails: billingDetails,
  });
  const {
    resetNotifications,
    scrollToField,
    setScrollToField,
    resetBusinessInfoForPrivateCustomer,
  } = useCheckoutAddressInstance;



  const setVatNumber = async (value: string) => {

    // Re-emitting the same number (the VAT input does it on every blur) leaves the autofill
    // that produced the current address intact - only a different number invalidates it.
    if (vatNumber.value === value) {

      return;

    }

    resetAddressAutofill();

    await setAddressVatNumber(value);

  };

  const areFieldValuesEqual = ({
    field,
    nextAddress,
    previousAddress,
  }: {
    field: keyof Address;
    nextAddress: Partial<Address>;
    previousAddress: Partial<Address>;
  }) => {

    return (nextAddress[field] ?? '') === (previousAddress[field] ?? '');

  };

  const persistBillingAddress = asyncDebounce(async (field?: string) => {

    if (!field || !cart.value) {

      return;

    }

    const typedField = field as keyof Address;

    if (areFieldValuesEqual({
      field: typedField,
      nextAddress: billingDetails.value,
      previousAddress: persistedBillingDetails.value,
    })) {

      return;

    }

    const shippingAddress = shippingAddressType.value === ShippingAddressType.SAME_AS_BILLING
      ? truncateAddressFields({ ...billingDetails.value, id: undefined }, ADDRESS_TYPE.Shipping)
      : undefined;

    await setCartBillingDetails(billingDetails.value, {
      vatNumber: vatNumber.value,
      vatResult: vatResult.value,
      shippingAddress,
    });

    persistedBillingDetails.value = { ...billingDetails.value };

  }, ADDRESS_PERSISTENCE_DEBOUNCE_MS);

  const handleResetBusinessInfo = async () => {

    await resetBusinessInfoForPrivateCustomer({
      setVatNumber,
      clientType: clientType.value,
      setAddressDetailsMutation: setBillingDetailsMutation,
    });

  };

  const validateSelectedBillingVatDetails = async (forceValidate = false): Promise<void> => {

    const shouldValidateAddress = hasRegistryAddressToValidate(billingDetails.value);

    // The address always goes along, even when it is not to be judged against the registry -
    // the cart's billing address must track whatever address the validation status is being
    // recorded for, or the two end up out of sync (e.g. after switching to a fresh address).
    const hasValidated = await validateVatNumber({
      vatNumber: vatNumber.value,
      forceValidate,
      address: billingDetails.value,
    });

    // A stored result was already acted on when it landed; acting on it again would overwrite the
    // edits the user made since.
    if (!hasValidated) {

      return;

    }

    if (shouldClearRegistryAddress.value) {

      await clearVatAutofillAddress();

      return;

    }

    if (!shouldValidateAddress && companyAddress.value) {

      await applyVatAutofillAddress(companyAddress.value);

    }

  };

  const handleBillingBeforeSubmit = async (isValid: boolean,
    fields: Record<string, ValidationProvider> | undefined) => {

    resetNotifications();

    if (isValid && 'postalCode' in billingDetails.value) {

      await handleResetBusinessInfo();

      if (clientType.value === CheckoutClientType.COMPANY && !vatResult.value) {

        await validateSelectedBillingVatDetails();

      }

    } else {

      registerAddressErrors(fields);

    }

  };

  const load = async () => {

    await loadUser();
    await loadBilling();

    await setBillingDetails(getInitialBillingAddress(address.value || defaultAddress));

  };

  const setBillingDetailsField = async (field: string, value: string) => {

    // Only the fields the autofill wrote can contradict the notice - the user still has to type
    // the ones it left empty, and doing so is no reason to retire it.
    if (VAT_AUTOFILL_ADDRESS_FIELDS.includes(field as keyof VatCompanyAddress)) {

      dismissAutofillNotice();

    }

    await setBillingDetails({
      ...billingDetails.value,
      [field]: value,
    }, {
      isFullAddress: false,
    });

  };

  const resetBillingDetailsForDifferentAddress = async () => {

    await setBillingDetails({ ...defaultAddress });

  };

  const handleBillingAddressBlur = async (field?: string) => {

    if (isBillingAddressValidationEnabled.value) {

      await validateAddress(billingDetails.value, field);

    }

    await persistBillingAddress(field);

  };

  /**
   * The registry answer as it should land in the form: the fields the registry never spoke for are
   * dropped rather than written as empty, so a postal code the customer already typed on a market
   * whose registry never states one survives the autofill instead of being wiped by it.
   */
  const getAutofillFields = (address: VatCompanyAddress): Partial<VatCompanyAddress> => {

    return Object.fromEntries(
      Object.entries(address)
        .filter(([field]) => !isFieldUnanswered(field as keyof VatCompanyAddress, address)),
    );

  };

  const applyVatAutofillAddress = async (address: VatCompanyAddress) => {

    await setBillingDetails({
      ...billingDetails.value,
      ...getAutofillFields(address),
    }, { isFullAddress: false, isVatAutofill: true });

    // The fields now hold the registry address, so the first blur on each of them has nothing
    // new to ask about.
    markAddressAsValidated(billingDetails.value);

  };

  /**
   * The mirror image of applyVatAutofillAddress: a validation that reached no verdict has nothing
   * to say about the registry fields, so they go back to empty. The fields the registry never
   * spoke for - the name, the phone - stay as they are.
   *
   * Only the "we filled this in for you" notice is retired, not the record that we ever did: the
   * hint offering the autofill is addressed to someone who has not entered a VAT number yet, and
   * bringing it back to someone who just did would read as a step backwards.
   */
  const clearVatAutofillAddress = async () => {

    const emptyRegistryFields = Object.fromEntries(
      VAT_AUTOFILL_ADDRESS_FIELDS.map(field => [field, '']),
    );

    await setBillingDetails({
      ...billingDetails.value,
      ...emptyRegistryFields,
    }, { isFullAddress: false });

    dismissAutofillNotice();

  };

  const setBillingDetails = async (
    address: Partial<Address>,
    {
      isFullAddress = true,
      isVatAutofill = false,
    }: {
      isFullAddress?: boolean;
      isVatAutofill?: boolean;
    } = {},
  ) => {

    const normalizedAddressCountry = address?.country?.toLowerCase();
    const normalizedCountry = country.value?.toLowerCase();

    if (normalizedAddressCountry && normalizedCountry && normalizedAddressCountry !== normalizedCountry) {

      await setCartBillingDetails({ country: country.value }, { vatNumber: '' });

      return;

    }

    const addressWithCountry = (!normalizedAddressCountry && country.value)
      ? { ...address, country: country.value }
      : { ...address };


    if (isFullAddress) {

      await setClientTypeFromAddress(addressWithCountry);

    }

    setBillingDetailsMutation(addressWithCountry);

    if (isFullAddress) {

      persistedBillingDetails.value = { ...addressWithCountry };

    }

    if (isVatAutofill) {

      setAddressAutofilled();

      return;

    }

    if (isFullAddress) {

      resetAddressAutofill();
      await setVatNumber(getVatNumberFromCustomerAddress(billingDetails.value));

      await validateSelectedBillingVatDetails();

    }

  };


  const getInitialBillingAddress = (address: Partial<Address>) => {

    if (!address.postalCode) {

      return defaultAddress;

    }

    return { ...address, id: undefined };

  };


  return {
    ...useCheckoutAddressInstance,
    load,
    vatNumber,
    setVatNumber,
    setBillingDetails,
    resetBillingDetailsForDifferentAddress,
    setBillingDetailsField,
    handleBillingAddressBlur,
    handleBillingBeforeSubmit,
    billingDetails,
    applyVatAutofillAddress,
    clearVatAutofillAddress,
    validateSelectedBillingVatDetails,
    isAddressAutofilled,
    hasAddressBeenAutofilled,
    addressMismatches,
    validatingAddressFields,
    scrollToField,
    setScrollToField,
  };

};

export default useCheckoutBilling;
